import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { toast } from 'react-toastify';
import DatePickerField from '../../components/common/DatePickerField';
import InspectionDetailModal from '../../components/common/InspectionDetailModal';
import projectService from '../../services/projectService';
import incidentService from '../../services/incidentService';
import inspectionService from '../../services/inspectionService';
import achievementService from '../../services/achievementService';
import { useAuth } from '../../contexts/AuthContext';
import { formatCategory, getTrackedCategoryOptions } from '../inspections/categoryOptions';
import './Reports.css';

// Incident category/severity are a fixed backend enum (distinct from the
// org-configurable categories used by MonitoringParameter/Inspection/
// ComplianceFinding), so they get their own label maps here.
const INCIDENT_CATEGORY_LABEL = {
  osh: 'OHS',
  environmental: 'Environmental',
  community: 'Community/Stakeholder',
  labor: 'Labor',
  security: 'Security',
  property: 'Property',
};

const INCIDENT_SEVERITY_LABEL = {
  fatal: 'Fatal',
  major: 'Major',
  moderate: 'Moderate',
  minor: 'Minor',
  near_miss: 'Near Miss',
};

const SEVERITY_COLOR = {
  fatal: '#7f1d1d',
  major: '#ef4444',
  moderate: '#f59e0b',
  minor: '#3b82f6',
  near_miss: '#6b7280',
};

const RAG_BAND_COLOR = { GREEN: '#16a34a', AMBER: '#f59e0b', RED: '#ef4444' };
const FINDING_STATUS_LABEL = { not_done: 'Not Done', partially_done: 'Partially Done', done: 'Done' };
const CATEGORY_COLORS = ['#16a34a', '#10b981', '#3b82f6', '#f59e0b', '#6b7280', '#8b5cf6', '#ec4899', '#14b8a6'];

// Client-side mirror of ComplianceService's weighting, used only to display
// the per-row Weight/Score breakdown - the backend's aggregate score/band
// (fetched via inspectionService.getScore) is always the source of truth.
const FINDING_SEVERITY_WEIGHT = { critical: 5, major: 3, minor: 1 };
const FINDING_STATUS_PROGRESS = { not_done: 0, partially_done: 50, done: 100 };

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

function getDateBounds(dateRange, customDateRange) {
  const now = new Date();
  switch (dateRange) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'week': {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case 'month':
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: endOfDay(now) };
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return { start: new Date(now.getFullYear(), q * 3, 1), end: endOfDay(now) };
    }
    case 'year':
      return { start: new Date(now.getFullYear(), 0, 1), end: endOfDay(now) };
    case 'last30': {
      const start = new Date(now);
      start.setDate(now.getDate() - 30);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case 'last90': {
      const start = new Date(now);
      start.setDate(now.getDate() - 90);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case 'custom':
      return {
        start: customDateRange.start ? startOfDay(new Date(customDateRange.start)) : null,
        end: customDateRange.end ? endOfDay(new Date(customDateRange.end)) : null,
      };
    default:
      return { start: null, end: null };
  }
}

function withinBounds(dateStr, bounds) {
  if (!dateStr) return false;
  if (!bounds.start && !bounds.end) return true;
  const d = new Date(dateStr);
  if (bounds.start && d < bounds.start) return false;
  if (bounds.end && d > bounds.end) return false;
  return true;
}

function lastNMonths(n) {
  const months = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('default', { month: 'short' }),
    });
  }
  return months;
}

function monthKey(dateStr) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString() : '—');

const Reports = () => {
  const navigate = useNavigate();
  const { organisation } = useAuth();

  const [projects, setProjects] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [findings, setFindings] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [scoresByProject, setScoresByProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [savingAchievement, setSavingAchievement] = useState(false);

  const [dateRange, setDateRange] = useState('month');
  const [selectedProjects, setSelectedProjects] = useState(['all']);
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [activeReportType, setActiveReportType] = useState('internal');
  const [showDateModal, setShowDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [drillDownData, setDrillDownData] = useState(null);
  const [viewingInspectionId, setViewingInspectionId] = useState(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [activeTab, setActiveTab] = useState('visualizations');
  const projectSelectorRef = useRef(null);
  const categorySelectorRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [projectData, incidentData, inspectionData, findingData, achievementData] = await Promise.all([
          projectService.getAll(),
          incidentService.getAll(),
          inspectionService.getAll(),
          inspectionService.getFindings(),
          achievementService.getAll(),
        ]);
        setProjects(projectData.projects || []);
        setIncidents(incidentData.incidents || []);
        setInspections(inspectionData.inspections || []);
        setFindings(findingData.findings || []);
        setAchievements(achievementData.achievements || []);
      } catch (error) {
        console.error('Failed to load report data:', error);
        toast.error(error.response?.data?.error || 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Compliance score is a per-project calculation on the backend, so it's
  // fetched once per project here and filtered client-side like everything else.
  useEffect(() => {
    if (projects.length === 0) return;
    (async () => {
      const entries = await Promise.all(projects.map(async (p) => {
        try {
          const data = await inspectionService.getScore({ projectId: p.id });
          return [p.id, data];
        } catch (error) {
          console.error(`Failed to load compliance score for project ${p.id}:`, error);
          return [p.id, null];
        }
      }));
      setScoresByProject(Object.fromEntries(entries));
    })();
  }, [projects]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectSelectorRef.current && !projectSelectorRef.current.contains(event.target)) {
        setShowProjectSelector(false);
      }
      if (categorySelectorRef.current && !categorySelectorRef.current.contains(event.target)) {
        setShowCategorySelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedProjectIds = useMemo(() => (
    selectedProjects.includes('all') ? projects.map((p) => p.id) : selectedProjects
  ), [selectedProjects, projects]);

  const dateBounds = useMemo(() => getDateBounds(dateRange, customDateRange), [dateRange, customDateRange]);

  const availableCategories = useMemo(() => {
    const cats = new Map();
    // Incident category is a fixed backend enum, unrelated to org setup - see
    // the comment on INCIDENT_CATEGORY_LABEL above - so it's always offered.
    Object.entries(INCIDENT_CATEGORY_LABEL).forEach(([id, label]) => cats.set(id, label));
    // Every category this org actually tracks (plus its custom ones) is
    // always offered, not just ones that happen to already have data -
    // otherwise a newly-tracked category has no way to be filtered on yet.
    getTrackedCategoryOptions(organisation?.trackingCategories, organisation?.customCategories || [])
      .forEach((c) => cats.set(c.value, c.label));
    // Still surface anything already in the data even if it's since been
    // untracked, so historical records remain filterable/visible.
    inspections.forEach((i) => {
      if (i.category && !cats.has(i.category)) cats.set(i.category, formatCategory(i.category));
    });
    findings.forEach((f) => {
      if (f.category && !cats.has(f.category)) cats.set(f.category, formatCategory(f.category));
    });
    return Array.from(cats.entries()).map(([id, label]) => ({ id, label }));
  }, [organisation, inspections, findings]);

  const filteredIncidents = useMemo(() => incidents.filter((i) => (
    selectedProjectIds.includes(i.projectId)
    && (selectedCategories.includes('all') || selectedCategories.includes(i.category))
    && withinBounds(i.occurredAt, dateBounds)
  )), [incidents, selectedProjectIds, selectedCategories, dateBounds]);

  const filteredInspections = useMemo(() => inspections.filter((i) => (
    selectedProjectIds.includes(i.projectId)
    && (selectedCategories.includes('all') || selectedCategories.includes(i.category))
    && withinBounds(i.submittedAt, dateBounds)
  )), [inspections, selectedProjectIds, selectedCategories, dateBounds]);

  const filteredFindings = useMemo(() => findings.filter((f) => (
    selectedProjectIds.includes(f.projectId)
    && (selectedCategories.includes('all') || selectedCategories.includes(f.category))
    && withinBounds(f.dateRaised, dateBounds)
  )), [findings, selectedProjectIds, selectedCategories, dateBounds]);

  const filteredAchievements = useMemo(() => achievements.filter((a) => (
    selectedProjectIds.includes(a.projectId)
    && (selectedCategories.includes('all') || selectedCategories.includes(a.category))
    && withinBounds(a.dateRecorded, dateBounds)
  )), [achievements, selectedProjectIds, selectedCategories, dateBounds]);

  // Closed Register: the permanent evidence log of findings that have been
  // fully resolved (status='done'). Unlike the active register above, this
  // is meant to be a durable record you can hand to a client/auditor at any
  // time, not just a filtered view - so it's sorted by when each item was
  // actually closed, not when it was raised.
  const closedFindings = useMemo(() => (
    filteredFindings
      .filter((f) => f.status === 'done')
      .sort((a, b) => new Date(b.dateClosed || 0) - new Date(a.dateClosed || 0))
  ), [filteredFindings]);

  const handleCreateAchievement = async (data) => {
    try {
      setSavingAchievement(true);
      const result = await achievementService.create(data);
      setAchievements((prev) => [result.achievement, ...prev]);
      toast.success('Achievement recorded successfully');
      setShowAchievementModal(false);
    } catch (error) {
      console.error('Failed to record achievement:', error);
      toast.error(error.response?.data?.error || 'Failed to record achievement');
    } finally {
      setSavingAchievement(false);
    }
  };

  const handleDeleteAchievement = async (achievement) => {
    if (!window.confirm('Delete this achievement record?')) return;
    try {
      await achievementService.delete(achievement.id);
      setAchievements((prev) => prev.filter((a) => a.id !== achievement.id));
      toast.success('Achievement deleted');
    } catch (error) {
      console.error('Failed to delete achievement:', error);
      toast.error(error.response?.data?.error || 'Failed to delete achievement');
    }
  };

  const isSingleProject = selectedProjectIds.length === 1;
  const isMultipleProjects = selectedProjectIds.length > 1;

  const filteredMetrics = useMemo(() => {
    const relevantScores = selectedProjectIds.map((id) => scoresByProject[id]).filter(Boolean);
    const avgCompliance = relevantScores.length
      ? Math.round((relevantScores.reduce((sum, s) => sum + s.score, 0) / relevantScores.length) * 100)
      : null;

    const majorIncidents = incidents.filter((i) => (
      selectedProjectIds.includes(i.projectId) && ['major', 'fatal'].includes(i.severity)
    ));
    const daysSinceLastMajor = majorIncidents.length
      ? Math.floor((Date.now() - Math.max(...majorIncidents.map((i) => new Date(i.occurredAt).getTime()))) / (1000 * 60 * 60 * 24))
      : null;

    return {
      avgCompliance,
      totalIncidents: filteredIncidents.length,
      totalInspections: filteredInspections.length,
      daysSinceLastMajor,
    };
  }, [selectedProjectIds, scoresByProject, incidents, filteredIncidents, filteredInspections]);

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setShowDateModal(true);
    } else {
      setDateRange(value);
    }
  };

  const handleProjectToggle = (projectId) => {
    if (projectId === 'all') {
      setSelectedProjects(['all']);
      return;
    }
    setSelectedProjects((prev) => {
      if (prev.includes('all')) return [projectId];
      const isSelected = prev.includes(projectId);
      if (isSelected) {
        const next = prev.filter((id) => id !== projectId);
        return next.length === 0 ? ['all'] : next;
      }
      return [...prev, projectId];
    });
  };

  const handleCategoryToggle = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedCategories(['all']);
      return;
    }
    setSelectedCategories((prev) => {
      if (prev.includes('all')) return [categoryId];
      const isSelected = prev.includes(categoryId);
      if (isSelected) {
        const next = prev.filter((id) => id !== categoryId);
        return next.length === 0 ? ['all'] : next;
      }
      return [...prev, categoryId];
    });
  };

  // ---- Charts (all built from real, filtered data) ----

  const complianceScoreOption = useMemo(() => {
    const rows = selectedProjectIds
      .map((id) => {
        const project = projects.find((p) => p.id === id);
        const score = scoresByProject[id];
        return {
          name: project?.name || id,
          pct: score ? Math.round(score.score * 100) : null,
          band: score?.band || null,
        };
      })
      .filter((r) => r.pct !== null);

    return {
      title: { text: 'Compliance Score by Project', left: 'center', textStyle: { fontSize: 16, fontWeight: 600, color: '#111827' } },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '8%', bottom: '3%', containLabel: true },
      xAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
      yAxis: { type: 'category', data: rows.map((r) => r.name) },
      series: [{
        name: 'Compliance Score',
        type: 'bar',
        barWidth: '55%',
        data: rows.map((r) => ({ value: r.pct, itemStyle: { color: RAG_BAND_COLOR[r.band] || '#6b7280' } })),
        label: { show: true, position: 'right', formatter: '{c}%' },
      }],
    };
  }, [selectedProjectIds, projects, scoresByProject]);

  const incidentsByCategoryOption = useMemo(() => {
    const counts = {};
    filteredIncidents.forEach((i) => { counts[i.category] = (counts[i.category] || 0) + 1; });
    const data = Object.entries(counts).map(([cat, count], idx) => ({
      value: count,
      name: INCIDENT_CATEGORY_LABEL[cat] || cat,
      itemStyle: { color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length] },
    }));
    return {
      title: { text: 'Incidents by Category', left: 'center', top: 10, textStyle: { fontSize: 16, fontWeight: 600, color: '#111827' } },
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 5, left: 'center', textStyle: { fontSize: 12 } },
      series: [{
        name: 'Incidents',
        type: 'pie',
        radius: ['35%', '55%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        data: data.length > 0 ? data : [{ value: 1, name: 'No Data', itemStyle: { color: '#e5e7eb' } }],
      }],
    };
  }, [filteredIncidents]);

  const incidentsBySeverityOption = useMemo(() => {
    const counts = {};
    filteredIncidents.forEach((i) => { counts[i.severity] = (counts[i.severity] || 0) + 1; });
    const data = Object.entries(counts).map(([sev, count]) => ({
      value: count,
      name: INCIDENT_SEVERITY_LABEL[sev] || sev,
      itemStyle: { color: SEVERITY_COLOR[sev] || '#6b7280' },
    }));
    return {
      title: { text: 'Incidents by Severity', left: 'center', top: 10, textStyle: { fontSize: 16, fontWeight: 600, color: '#111827' } },
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 5, left: 'center', textStyle: { fontSize: 12 } },
      series: [{
        name: 'Severity',
        type: 'pie',
        radius: '50%',
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        label: { show: false },
        data: data.length > 0 ? data : [{ value: 1, name: 'No Data', itemStyle: { color: '#e5e7eb' } }],
        emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.5)' } },
      }],
    };
  }, [filteredIncidents]);

  const incidentTrendOption = useMemo(() => {
    const months = lastNMonths(12);
    const total = Object.fromEntries(months.map((m) => [m.key, 0]));
    const major = Object.fromEntries(months.map((m) => [m.key, 0]));
    filteredIncidents.forEach((i) => {
      const key = monthKey(i.occurredAt);
      if (key in total) {
        total[key] += 1;
        if (['fatal', 'major'].includes(i.severity)) major[key] += 1;
      }
    });
    return {
      title: { text: 'Incident Frequency Trend', left: 'center', textStyle: { fontSize: 16, fontWeight: 600, color: '#111827' } },
      tooltip: { trigger: 'axis' },
      legend: { data: ['Total Incidents', 'Major/Fatal'], bottom: 10 },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', boundaryGap: false, data: months.map((m) => m.label) },
      yAxis: { type: 'value' },
      series: [
        {
          name: 'Total Incidents', type: 'line', smooth: true, data: months.map((m) => total[m.key]), itemStyle: { color: '#3b82f6' }, areaStyle: { color: 'rgba(59, 130, 246, 0.1)' },
        },
        {
          name: 'Major/Fatal', type: 'line', smooth: true, data: months.map((m) => major[m.key]), itemStyle: { color: '#ef4444' },
        },
      ],
    };
  }, [filteredIncidents]);

  const findingsStatusOption = useMemo(() => {
    const months = lastNMonths(6);
    const byMonth = Object.fromEntries(months.map((m) => [m.key, {
      not_done: 0, partially_done: 0, done: 0,
    }]));
    filteredFindings.forEach((f) => {
      const key = monthKey(f.dateRaised);
      if (byMonth[key]) byMonth[key][f.status] = (byMonth[key][f.status] || 0) + 1;
    });
    return {
      title: { text: 'Compliance Findings Raised, by Status', left: 'center', textStyle: { fontSize: 16, fontWeight: 600, color: '#111827' } },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: [FINDING_STATUS_LABEL.done, FINDING_STATUS_LABEL.partially_done, FINDING_STATUS_LABEL.not_done], bottom: 10 },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: months.map((m) => m.label) },
      yAxis: { type: 'value' },
      series: [
        {
          name: FINDING_STATUS_LABEL.done, type: 'bar', stack: 'total', data: months.map((m) => byMonth[m.key].done), itemStyle: { color: '#16a34a' },
        },
        {
          name: FINDING_STATUS_LABEL.partially_done, type: 'bar', stack: 'total', data: months.map((m) => byMonth[m.key].partially_done), itemStyle: { color: '#f59e0b' },
        },
        {
          name: FINDING_STATUS_LABEL.not_done, type: 'bar', stack: 'total', data: months.map((m) => byMonth[m.key].not_done), itemStyle: { color: '#ef4444' },
        },
      ],
    };
  }, [filteredFindings]);

  const handleCategoryChartClick = (params) => {
    const entry = Object.entries(INCIDENT_CATEGORY_LABEL).find(([, label]) => label === params.name);
    const catId = entry ? entry[0] : params.name;
    setDrillDownData({ label: params.name, items: filteredIncidents.filter((i) => i.category === catId) });
  };

  const handleSeverityChartClick = (params) => {
    const entry = Object.entries(INCIDENT_SEVERITY_LABEL).find(([, label]) => label === params.name);
    const sevId = entry ? entry[0] : params.name;
    setDrillDownData({ label: params.name, items: filteredIncidents.filter((i) => i.severity === sevId) });
  };

  const handleExportPlaceholder = (format) => {
    toast.info(`${format} export is not implemented yet`);
  };

  if (loading) {
    return (
      <div className="reports-page">
        <div className="reports-empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading report data...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="reports-header">
        <div className="header-content">
          <div className="header-title">
            <h1>
              <i className="fas fa-chart-bar"></i>
              Reports & Analytics
            </h1>
            <p className="header-subtitle">Compliance reporting and data visualization</p>
          </div>
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => handleExportPlaceholder('PDF')}>
              <i className="fas fa-file-pdf"></i> Export PDF
            </button>
            <button className="btn-primary-green" onClick={() => handleExportPlaceholder('Excel')}>
              <i className="fas fa-file-excel"></i> Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      <div className="reports-filters">
        <div className="filter-section">
          <label>Date Range</label>
          <select className="filter-select" value={dateRange} onChange={handleDateRangeChange}>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
            <option value="last30">Last 30 Days</option>
            <option value="last90">Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        <div className="filter-section">
          <label>Projects</label>
          <div className="multi-select-wrapper" ref={projectSelectorRef}>
            <button className="multi-select-trigger" onClick={() => setShowProjectSelector(!showProjectSelector)}>
              <span>{selectedProjects.includes('all') ? 'All Projects' : `${selectedProjects.length} selected`}</span>
              <i className={`fas fa-chevron-${showProjectSelector ? 'up' : 'down'}`}></i>
            </button>
            {showProjectSelector && (
              <div className="multi-select-dropdown">
                <label className="multi-select-option">
                  <input type="checkbox" checked={selectedProjects.includes('all')} onChange={() => handleProjectToggle('all')} />
                  <span>All Projects</span>
                </label>
                {projects.map((p) => (
                  <label key={p.id} className="multi-select-option">
                    <input type="checkbox" checked={selectedProjects.includes(p.id)} onChange={() => handleProjectToggle(p.id)} />
                    <span>{p.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="filter-section">
          <label>Categories</label>
          <div className="multi-select-wrapper" ref={categorySelectorRef}>
            <button className="multi-select-trigger" onClick={() => setShowCategorySelector(!showCategorySelector)}>
              <span>{selectedCategories.includes('all') ? 'All Categories' : `${selectedCategories.length} selected`}</span>
              <i className={`fas fa-chevron-${showCategorySelector ? 'up' : 'down'}`}></i>
            </button>
            {showCategorySelector && (
              <div className="multi-select-dropdown">
                <label className="multi-select-option">
                  <input type="checkbox" checked={selectedCategories.includes('all')} onChange={() => handleCategoryToggle('all')} />
                  <span>All Categories</span>
                </label>
                {availableCategories.map((c) => (
                  <label key={c.id} className="multi-select-option">
                    <input type="checkbox" checked={selectedCategories.includes(c.id)} onChange={() => handleCategoryToggle(c.id)} />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="filter-section">
          <label>Report Type</label>
          <div className="report-type-tabs">
            <button className={`tab-btn ${activeReportType === 'internal' ? 'active' : ''}`} onClick={() => setActiveReportType('internal')}>Internal</button>
            <button className={`tab-btn ${activeReportType === 'external' ? 'active' : ''}`} onClick={() => setActiveReportType('external')}>External</button>
            <button className={`tab-btn ${activeReportType === 'authority' ? 'active' : ''}`} onClick={() => setActiveReportType('authority')}>Authority</button>
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <div className="content-tabs">
        <button className={`content-tab ${activeTab === 'visualizations' ? 'active' : ''}`} onClick={() => setActiveTab('visualizations')}>
          <i className="fas fa-chart-line"></i> Visualizations
        </button>
        <button className={`content-tab ${activeTab === 'data' ? 'active' : ''}`} onClick={() => setActiveTab('data')}>
          <i className="fas fa-table"></i> Data Tables
        </button>
      </div>

      {/* Active Filters Indicator */}
      {(!selectedProjects.includes('all') || !selectedCategories.includes('all')) && (
        <div className="active-filters">
          <div className="filter-indicator">
            <i className="fas fa-filter"></i>
            <span>Active Filters:</span>
          </div>
          {!selectedProjects.includes('all') && selectedProjects.map((projId) => {
            const project = projects.find((p) => p.id === projId);
            return project ? (
              <div key={projId} className="filter-tag">
                <span>{project.name}</span>
                <button onClick={() => handleProjectToggle(projId)}><i className="fas fa-times"></i></button>
              </div>
            ) : null;
          })}
          {!selectedCategories.includes('all') && selectedCategories.map((catId) => {
            const category = availableCategories.find((c) => c.id === catId);
            return category ? (
              <div key={catId} className="filter-tag">
                <span>{category.label}</span>
                <button onClick={() => handleCategoryToggle(catId)}><i className="fas fa-times"></i></button>
              </div>
            ) : null;
          })}
          <button className="clear-filters-btn" onClick={() => { setSelectedProjects(['all']); setSelectedCategories(['all']); }}>
            Clear All
          </button>
        </div>
      )}

      {/* Visualizations Tab Content */}
      {activeTab === 'visualizations' && (
        <>
          <div className="key-metrics">
            <div className="metric-card clickable" onClick={() => navigate('/projects')}>
              <div className="metric-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="metric-details">
                <div className="metric-label">Avg Compliance Score</div>
                <div className="metric-value">{filteredMetrics.avgCompliance !== null ? `${filteredMetrics.avgCompliance}%` : 'N/A'}</div>
              </div>
              <div className="metric-nav-icon"><i className="fas fa-arrow-right"></i></div>
            </div>

            <div className="metric-card clickable" onClick={() => navigate('/incidents')}>
              <div className="metric-icon" style={{ background: '#fee2e2', color: '#ef4444' }}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div className="metric-details">
                <div className="metric-label">Total Incidents</div>
                <div className="metric-value">{filteredMetrics.totalIncidents}</div>
              </div>
              <div className="metric-nav-icon"><i className="fas fa-arrow-right"></i></div>
            </div>

            <div className="metric-card clickable" onClick={() => navigate('/inspections')}>
              <div className="metric-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                <i className="fas fa-clipboard-check"></i>
              </div>
              <div className="metric-details">
                <div className="metric-label">Inspections</div>
                <div className="metric-value">{filteredMetrics.totalInspections}</div>
              </div>
              <div className="metric-nav-icon"><i className="fas fa-arrow-right"></i></div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
                <i className="fas fa-calendar-check"></i>
              </div>
              <div className="metric-details">
                <div className="metric-label">Days Since Last Major Incident</div>
                <div className="metric-value">{filteredMetrics.daysSinceLastMajor !== null ? filteredMetrics.daysSinceLastMajor : '—'}</div>
              </div>
            </div>
          </div>

          <div className="charts-container">
            {activeReportType === 'internal' && (
              <>
                <div className="chart-card full">
                  <ReactECharts option={complianceScoreOption} style={{ height: '100%' }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={incidentsByCategoryOption} style={{ height: '100%' }} onEvents={{ click: handleCategoryChartClick }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={findingsStatusOption} style={{ height: '100%' }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={incidentTrendOption} style={{ height: '100%' }} notMerge />
                </div>
              </>
            )}

            {activeReportType === 'external' && (
              <>
                <div className="chart-card full">
                  <ReactECharts option={incidentTrendOption} style={{ height: '100%' }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={incidentsBySeverityOption} style={{ height: '100%' }} onEvents={{ click: handleSeverityChartClick }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={complianceScoreOption} style={{ height: '100%' }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={incidentsByCategoryOption} style={{ height: '100%' }} onEvents={{ click: handleCategoryChartClick }} notMerge />
                </div>
              </>
            )}

            {activeReportType === 'authority' && (
              <>
                <div className="chart-card full">
                  <ReactECharts option={complianceScoreOption} style={{ height: '100%' }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={incidentsBySeverityOption} style={{ height: '100%' }} onEvents={{ click: handleSeverityChartClick }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={incidentsByCategoryOption} style={{ height: '100%' }} onEvents={{ click: handleCategoryChartClick }} notMerge />
                </div>
                <div className="chart-card">
                  <ReactECharts option={findingsStatusOption} style={{ height: '100%' }} notMerge />
                </div>
              </>
            )}

            {!isMultipleProjects && !isSingleProject && (
              <div className="reports-empty-state" style={{ gridColumn: '1 / -1' }}>
                <i className="fas fa-folder-open"></i>
                <h3>No projects yet</h3>
                <p>Create a project to start seeing reports here.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Data Tables Tab Content */}
      {activeTab === 'data' && (
        <div className="data-tables-container">
          <div className="data-table-section">
            <div className="table-header">
              <h3><i className="fas fa-exclamation-triangle"></i> Incidents</h3>
              <button className="btn-secondary" onClick={() => navigate('/incidents')}>
                View Full Page <i className="fas fa-arrow-right"></i>
              </button>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th><th>Date</th><th>Project</th><th>Category</th><th>Severity</th><th>Description</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIncidents.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#6b7280' }}>No incidents match the current filters</td></tr>
                  ) : filteredIncidents.map((incident) => (
                    <tr key={incident.id}>
                      <td className="incident-id-cell">{incident.incidentNumber}</td>
                      <td>{formatDate(incident.occurredAt)}</td>
                      <td>{incident.project?.name || '—'}</td>
                      <td><span className="category-badge">{INCIDENT_CATEGORY_LABEL[incident.category] || incident.category}</span></td>
                      <td><span className={`severity-badge ${incident.severity}`}>{INCIDENT_SEVERITY_LABEL[incident.severity] || incident.severity}</span></td>
                      <td className="description-cell">{incident.description || incident.title}</td>
                      <td><span className="status-badge">{incident.status.replace(/_/g, ' ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="data-table-section">
            <div className="table-header">
              <h3><i className="fas fa-check-circle"></i> Compliance Findings Register</h3>
              <button className="btn-secondary" onClick={() => navigate('/inspections')}>
                View Inspections <i className="fas fa-arrow-right"></i>
              </button>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th><th>Project</th><th>Category</th><th>Severity</th><th>Status</th><th>Weight</th><th>Score</th><th>RAG</th><th>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFindings.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: '#6b7280' }}>No compliance findings match the current filters</td></tr>
                  ) : filteredFindings.map((finding) => {
                    const weight = FINDING_SEVERITY_WEIGHT[finding.severity] || 1;
                    const progress = FINDING_STATUS_PROGRESS[finding.status] ?? 0;
                    const weightedScore = (weight * progress) / 100;
                    return (
                      <tr key={finding.id}>
                        <td>{finding.title}</td>
                        <td>{finding.project?.name || '—'}</td>
                        <td><span className="category-badge">{formatCategory(finding.category)}</span></td>
                        <td>
                          <span className={`compliance-score ${finding.severity === 'critical' ? 'low' : finding.severity === 'major' ? 'medium' : 'high'}`}>
                            {finding.severity}
                          </span>
                        </td>
                        <td>{FINDING_STATUS_LABEL[finding.status] || finding.status}</td>
                        <td>{weight}</td>
                        <td>{weightedScore} / {weight}</td>
                        <td><span className={`rag-badge rag-${finding.rag.toLowerCase()}`}>{finding.rag}</span></td>
                        <td>{finding.deadline ? formatDate(finding.deadline) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="data-table-section">
            <div className="table-header">
              <div>
                <h3><i className="fas fa-archive"></i> Closed Register</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                  Permanent evidence log - every finding marked Done, with the date it was closed and how it was fixed. Kept as proof even after the active register moves on.
                </p>
              </div>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date Closed</th><th>Project</th><th>Category</th><th>Severity</th><th>Issue</th><th>Corrective Action</th>
                  </tr>
                </thead>
                <tbody>
                  {closedFindings.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>No findings closed out yet - resolved items will show up here permanently once marked Done</td></tr>
                  ) : closedFindings.map((finding) => (
                    <tr key={finding.id}>
                      <td>{formatDate(finding.dateClosed)}</td>
                      <td>{finding.project?.name || '—'}</td>
                      <td><span className="category-badge">{formatCategory(finding.category)}</span></td>
                      <td>
                        <span className={`compliance-score ${finding.severity === 'critical' ? 'low' : finding.severity === 'major' ? 'medium' : 'high'}`}>
                          {finding.severity}
                        </span>
                      </td>
                      <td>{finding.title}</td>
                      <td className="description-cell">{finding.correctiveAction || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="data-table-section">
            <div className="table-header">
              <h3><i className="fas fa-exclamation-triangle"></i> Incidents</h3>
              <button className="btn-secondary" onClick={() => navigate('/inspections')}>
                View Full Page <i className="fas fa-arrow-right"></i>
              </button>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Project</th><th>Category</th><th>Location</th><th>Compliance %</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInspections.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>No inspections match the current filters</td></tr>
                  ) : filteredInspections.map((inspection) => (
                    <tr key={inspection.id} style={{ cursor: 'pointer' }} onClick={() => setViewingInspectionId(inspection.id)}>
                      <td>{formatDate(inspection.submittedAt)}</td>
                      <td>{inspection.project?.name || '—'}</td>
                      <td><span className="category-badge">{formatCategory(inspection.category)}</span></td>
                      <td>{inspection.location || '—'}</td>
                      <td>
                        <span className={`compliance-score ${inspection.completionPercentage < 75 ? 'low' : inspection.completionPercentage < 90 ? 'medium' : 'high'}`}>
                          {inspection.completionPercentage}%
                        </span>
                      </td>
                      <td>
                        <span className={`compliance-score ${inspection.overallCompliance === 'compliant' ? 'high' : inspection.overallCompliance === 'partial' ? 'medium' : 'low'}`}>
                          {inspection.overallCompliance.replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="data-table-section">
            <div className="table-header">
              <h3><i className="fas fa-award"></i> Achievements / Good Practice</h3>
              <button className="btn-primary-green" onClick={() => setShowAchievementModal(true)}>
                <i className="fas fa-plus"></i> Add Achievement
              </button>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Project</th><th>Category</th><th>Description</th><th>Recorded By</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAchievements.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>No achievements recorded yet - positive recognition shows up here, separate from compliance findings</td></tr>
                  ) : filteredAchievements.map((achievement) => (
                    <tr key={achievement.id}>
                      <td>{formatDate(achievement.dateRecorded)}</td>
                      <td>{achievement.project?.name || '—'}</td>
                      <td><span className="category-badge">{formatCategory(achievement.category)}</span></td>
                      <td className="description-cell">{achievement.description}</td>
                      <td>{achievement.recorder ? `${achievement.recorder.firstName} ${achievement.recorder.lastName}` : '—'}</td>
                      <td>
                        <button className="close-btn" title="Delete" onClick={() => handleDeleteAchievement(achievement)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <InspectionDetailModal inspectionId={viewingInspectionId} onClose={() => setViewingInspectionId(null)} />

      {showAchievementModal && (
        <AchievementModal
          isOpen={showAchievementModal}
          onClose={() => setShowAchievementModal(false)}
          onSave={handleCreateAchievement}
          projects={projects}
          saving={savingAchievement}
        />
      )}

      {/* Custom Date Range Modal */}
      {showDateModal && (
        <div className="modal-overlay" onClick={() => setShowDateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Custom Date Range</h3>
              <button className="close-btn" onClick={() => setShowDateModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Start Date</label>
                <DatePickerField
                  className="form-input"
                  value={customDateRange.start}
                  onChange={(iso) => setCustomDateRange({ ...customDateRange, start: iso })}
                  maxDate={customDateRange.end}
                  placeholder="Select start date"
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <DatePickerField
                  className="form-input"
                  value={customDateRange.end}
                  onChange={(iso) => setCustomDateRange({ ...customDateRange, end: iso })}
                  minDate={customDateRange.start}
                  placeholder="Select end date"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDateModal(false)}>Cancel</button>
              <button
                className="btn-primary-green"
                onClick={() => { setDateRange('custom'); setShowDateModal(false); }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drill-Down Modal */}
      {drillDownData && (
        <div className="modal-overlay" onClick={() => setDrillDownData(null)}>
          <div className="modal-content drill-down-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Incidents: {drillDownData.label}</h3>
              <button className="close-btn" onClick={() => setDrillDownData(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div className="drill-down-summary">
                <div className="summary-stat">
                  <span className="stat-label">Total:</span>
                  <span className="stat-value">{drillDownData.items.length}</span>
                </div>
              </div>

              <h4>Matching Incidents</h4>
              {drillDownData.items.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No incidents in this slice.</p>
              ) : (
                <div className="incidents-list">
                  {drillDownData.items.map((incident) => (
                    <div key={incident.id} className="incident-item">
                      <div className="incident-header">
                        <span className="incident-id">{incident.incidentNumber}</span>
                        <span className={`severity-badge ${incident.severity}`}>{INCIDENT_SEVERITY_LABEL[incident.severity] || incident.severity}</span>
                      </div>
                      <div className="incident-description">{incident.description || incident.title}</div>
                      <div className="incident-meta">
                        <span><i className="fas fa-calendar"></i> {formatDate(incident.occurredAt)}</span>
                        {incident.location && <span><i className="fas fa-map-marker-alt"></i> {incident.location}</span>}
                        {incident.project && <span><i className="fas fa-folder"></i> {incident.project.name}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDrillDownData(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AchievementModal = ({ isOpen, onClose, onSave, projects, saving }) => {
  const { organisation } = useAuth();

  // Only offer categories this org actually tracks (plus its custom ones).
  const categoryOptions = useMemo(() => (
    getTrackedCategoryOptions(organisation?.trackingCategories, organisation?.customCategories || [])
  ), [organisation]);

  const [formData, setFormData] = useState({
    projectId: '',
    category: '',
    description: '',
    dateRecorded: new Date().toISOString().slice(0, 10),
  });

  // Default to the first tracked category once options are known (they
  // depend on the async-loaded organisation, so aren't ready at mount).
  useEffect(() => {
    if (!formData.category && categoryOptions.length > 0) {
      setFormData((prev) => ({ ...prev, category: categoryOptions[0].value }));
    }
  }, [categoryOptions, formData.category]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.projectId || !formData.description.trim()) {
      toast.error('Please select a project and describe the achievement');
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3><i className="fas fa-award"></i> Record Achievement</h3>
          <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Project *</label>
              <select className="form-input" value={formData.projectId} onChange={handleChange('projectId')} required>
                <option value="">Select project...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select className="form-input" value={formData.category} onChange={handleChange('category')}>
                {categoryOptions.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <DatePickerField
                className="form-input"
                value={formData.dateRecorded}
                onChange={(iso) => setFormData((prev) => ({ ...prev, dateRecorded: iso }))}
                maxDate={new Date().toISOString().slice(0, 10)}
              />
            </div>
            <div className="form-group">
              <label>Description *</label>
              <textarea
                className="form-input"
                rows="3"
                value={formData.description}
                onChange={handleChange('description')}
                placeholder="e.g. Nine Old Kampala Road manholes fitted with permanent covers & signage"
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary-green" disabled={saving}>
              {saving ? 'Saving...' : 'Record Achievement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Reports;
